import React, { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_IMAGES = 3;

const EMPTY = {
  title: '',
  body: '',
  photo_urls: [],
  author: '',
  location: '',
  status: 'draft',
};

const ImpactStoriesManagement = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');
  const [aiLength, setAiLength] = useState('medium');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNotice, setAiNotice] = useState('');
  const [adjustingIdx, setAdjustingIdx] = useState(null); // index of photo whose focal point is being edited

  const authHeader = `Basic ${localStorage.getItem('crmAuth') || ''}`;

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/crm/stories`, { headers: { Authorization: authHeader } });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      setStories(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => {
    setForm({ ...EMPTY, author: localStorage.getItem('crmUsername') || '' });
    setEditing({});
  };

  const startEdit = (s) => {
    // Backend returns photo_urls; fall back to photo_url for older data
    const urls = Array.isArray(s.photo_urls) && s.photo_urls.length
      ? s.photo_urls
      : (s.photo_url ? [s.photo_url] : []);
    const positionsRaw = Array.isArray(s.photo_positions) ? s.photo_positions : [];
    const positions = urls.map((_, i) => positionsRaw[i] || '50% 50%');
    setForm({
      title: s.title || '',
      body: s.body || '',
      photo_urls: urls.slice(0, MAX_IMAGES),
      photo_positions: positions.slice(0, MAX_IMAGES),
      author: s.author || '',
      location: s.location || '',
      status: s.status || 'draft',
    });
    setEditing(s);
  };

  const cancel = () => { setEditing(null); setForm(EMPTY); setError(''); setAiKeywords(''); setAiNotice(''); };

  const generateDraft = async () => {
    if (!aiKeywords.trim()) {
      setError('Enter some keywords first (e.g., "young farmer Guinea, radio drama, forgave brother").');
      return;
    }
    setAiLoading(true);
    setError('');
    setAiNotice('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/crm/stories/ai-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify({
          keywords: aiKeywords,
          location: form.location || '',
          length: aiLength,
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `AI draft failed (${res.status})`);
      }
      const data = await res.json();
      setForm(f => ({ ...f, title: data.title || f.title, body: data.body || f.body, status: 'draft' }));
      setAiNotice('✨ Draft generated. Please review carefully, edit as needed, then Save as Draft or Save & Publish.');
    } catch (e) {
      setError(e.message || 'AI draft failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so same file can be re-picked after remove
    if (!file) return;
    if ((form.photo_urls || []).length >= MAX_IMAGES) {
      setError(`Maximum ${MAX_IMAGES} images allowed. Remove one to add another.`);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/crm/stories/upload-photo`, {
        method: 'POST',
        headers: { Authorization: authHeader },
        body: fd,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      setForm(f => ({
        ...f,
        photo_urls: [...(f.photo_urls || []), data.url].slice(0, MAX_IMAGES),
        photo_positions: [...(f.photo_positions || []), '50% 50%'].slice(0, MAX_IMAGES),
      }));
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (idx) => {
    setForm(f => ({
      ...f,
      photo_urls: (f.photo_urls || []).filter((_, i) => i !== idx),
      photo_positions: (f.photo_positions || []).filter((_, i) => i !== idx),
    }));
  };

  const movePhoto = (idx, delta) => {
    setForm(f => {
      const arr = [...(f.photo_urls || [])];
      const pos = [...(f.photo_positions || [])];
      const j = idx + delta;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      [pos[idx], pos[j]] = [pos[j] || '50% 50%', pos[idx] || '50% 50%'];
      return { ...f, photo_urls: arr, photo_positions: pos };
    });
  };

  const setPhotoPosition = (idx, position) => {
    setForm(f => {
      const pos = [...(f.photo_positions || [])];
      while (pos.length < (f.photo_urls || []).length) pos.push('50% 50%');
      pos[idx] = position;
      return { ...f, photo_positions: pos };
    });
  };

  const save = async (statusOverride = null) => {
    const nextStatus = statusOverride || form.status;
    if (!form.title.trim() || !form.body.trim()) {
      setError('Title and body are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const isEdit = editing && editing.id;
      const url = isEdit ? `${BACKEND_URL}/api/crm/stories/${editing.id}` : `${BACKEND_URL}/api/crm/stories`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify({ ...form, status: nextStatus }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Save failed (${res.status})`);
      }
      await load();
      cancel();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this story? This cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/crm/stories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: authHeader },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const togglePublish = async (s) => {
    const next = s.status === 'published' ? 'draft' : 'published';
    try {
      const res = await fetch(`${BACKEND_URL}/api/crm/stories/${s.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error(`Update failed (${res.status})`);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📖 Impact Stories</h2>
          <p className="text-gray-600 text-sm">Upload listener testimonies and transformation stories. Published stories appear on the public Impact Stories page.</p>
        </div>
        {!editing && (
          <button
            onClick={startNew}
            data-testid="story-add-button"
            className="px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary font-semibold"
          >
            + New Story
          </button>
        )}
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded border border-red-200" data-testid="story-error">{error}</div>}

      {editing && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4">{editing.id ? 'Edit Story' : 'New Story'}</h3>

          {/* AI Draft Generator */}
          <div className="mb-6 bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">✨</span>
              <h4 className="font-semibold text-purple-900">Generate a Draft with AI</h4>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Give the AI a few keywords or facts (who, where, what happened) and it will draft a full story for you to review. <strong>The AI does not publish automatically</strong> — you must read, edit if needed, and save.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
              <textarea
                rows={2}
                data-testid="ai-keywords-input"
                value={aiKeywords}
                onChange={(e) => setAiKeywords(e.target.value)}
                placeholder='e.g., "young mother, Foya Liberia, heard Kioo at midnight, reconciled with her sister, now attends church"'
                className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400"
              />
              <div className="flex md:flex-col gap-2">
                <select
                  value={aiLength}
                  onChange={(e) => setAiLength(e.target.value)}
                  data-testid="ai-length-input"
                  className="px-3 py-2 border border-purple-200 rounded-lg text-sm"
                >
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="long">Long</option>
                </select>
                <button
                  type="button"
                  onClick={generateDraft}
                  disabled={aiLoading}
                  data-testid="ai-generate-button"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-60 whitespace-nowrap"
                >
                  {aiLoading ? 'Generating…' : '✨ Generate'}
                </button>
              </div>
            </div>
            {aiNotice && (
              <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-sm" data-testid="ai-notice">
                {aiNotice}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                data-testid="story-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                placeholder="A short, compelling headline"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Story Body *</label>
              <textarea
                rows={8}
                data-testid="story-body-input"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                placeholder="Tell the full story — where the listener is from, what they heard on Kioo Radio, and how it changed their life."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author / Submitted by</label>
                <input
                  type="text"
                  data-testid="story-author-input"
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  data-testid="story-location-input"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                  placeholder="e.g., Foya, Liberia"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photos (up to {MAX_IMAGES})
              </label>
              <div className="flex flex-wrap gap-3 items-start">
                {(form.photo_urls || []).map((u, idx) => {
                  const src = u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
                  const pos = (form.photo_positions || [])[idx] || '50% 50%';
                  return (
                    <div key={`${u}-${idx}`} className="relative group" data-testid={`story-photo-preview-${idx}`}>
                      <img
                        src={src}
                        alt={`story ${idx + 1}`}
                        className="w-28 h-28 object-cover rounded-lg border"
                        style={{ objectPosition: pos }}
                        data-testid={`story-photo-thumb-${idx}`}
                      />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] bg-emerald-600 text-white rounded font-semibold">COVER</span>
                      )}
                      <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1 rounded-lg">
                        {idx > 0 && (
                          <button
                            type="button"
                            onClick={() => movePhoto(idx, -1)}
                            className="w-7 h-7 bg-white text-gray-700 rounded-full text-xs hover:bg-gray-100"
                            title="Move left"
                            data-testid={`story-photo-move-left-${idx}`}
                          >←</button>
                        )}
                        {idx < (form.photo_urls.length - 1) && (
                          <button
                            type="button"
                            onClick={() => movePhoto(idx, 1)}
                            className="w-7 h-7 bg-white text-gray-700 rounded-full text-xs hover:bg-gray-100"
                            title="Move right"
                            data-testid={`story-photo-move-right-${idx}`}
                          >→</button>
                        )}
                        <button
                          type="button"
                          onClick={() => setAdjustingIdx(idx)}
                          className="w-7 h-7 bg-amber-500 text-white rounded-full text-xs hover:bg-amber-600"
                          title="Adjust focal point (click to position face)"
                          data-testid={`story-photo-adjust-${idx}`}
                        >🎯</button>
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="w-7 h-7 bg-red-600 text-white rounded-full text-xs hover:bg-red-700"
                          title="Remove"
                          data-testid={`story-photo-remove-${idx}`}
                        >✕</button>
                      </div>
                    </div>
                  );
                })}
                {(form.photo_urls || []).length < MAX_IMAGES && (
                  <label className="w-28 h-28 rounded-lg border-2 border-dashed border-gray-300 hover:border-kioo-primary text-gray-400 hover:text-kioo-primary cursor-pointer flex flex-col items-center justify-center text-xs gap-1 transition-colors">
                    <span className="text-2xl">+</span>
                    <span>{uploading ? 'Uploading…' : 'Add photo'}</span>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      data-testid="story-photo-input"
                      onChange={handlePhoto}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                The first image is used as the cover on the card. Hover a photo to reorder, remove, or click 🎯 to set the focal point so faces stay in frame. Max 5MB each.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={() => save('draft')}
              disabled={saving}
              data-testid="story-save-draft-button"
              className="px-5 py-2 border border-kioo-primary text-kioo-primary rounded-lg hover:bg-emerald-50 font-semibold disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save as Draft'}
            </button>
            <button
              onClick={() => save('published')}
              disabled={saving}
              data-testid="story-publish-button"
              className="px-5 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary font-semibold disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save & Publish'}
            </button>
            <button
              onClick={cancel}
              className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading…</div>
      ) : stories.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-gray-100 text-gray-500">
          No stories yet. Click "+ New Story" to add your first testimony.
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-4 py-3">Photo</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Author</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {stories.map(s => {
                const urls = (s.photo_urls && s.photo_urls.length) ? s.photo_urls : (s.photo_url ? [s.photo_url] : []);
                const cover = urls[0];
                const coverSrc = cover ? (cover.startsWith('http') ? cover : `${BACKEND_URL}${cover}`) : null;
                return (
                <tr key={s.id} className="border-t border-gray-100" data-testid={`story-row-${s.id}`}>
                  <td className="px-4 py-2">
                    <div className="relative">
                      {coverSrc ? (
                        <img src={coverSrc} alt={s.title} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center text-lg">📖</div>
                      )}
                      {urls.length > 1 && (
                        <span className="absolute -top-1 -right-1 bg-kioo-primary text-white text-[10px] px-1.5 rounded-full font-bold">+{urls.length - 1}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 font-medium text-gray-900 max-w-md truncate">{s.title}</td>
                  <td className="px-4 py-2 text-gray-700">{s.author}</td>
                  <td className="px-4 py-2 text-gray-700">{s.location}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${s.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => togglePublish(s)}
                      data-testid={`story-toggle-${s.id}`}
                      className="text-blue-600 hover:underline mr-3"
                    >
                      {s.status === 'published' ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      onClick={() => startEdit(s)}
                      data-testid={`story-edit-${s.id}`}
                      className="text-kioo-primary hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(s.id)}
                      data-testid={`story-delete-${s.id}`}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adjustingIdx !== null && (form.photo_urls || [])[adjustingIdx] && (
        <FocalPointModal
          src={(form.photo_urls[adjustingIdx] || '').startsWith('http')
            ? form.photo_urls[adjustingIdx]
            : `${BACKEND_URL}${form.photo_urls[adjustingIdx]}`}
          initialPosition={(form.photo_positions || [])[adjustingIdx] || '50% 50%'}
          onSave={(pos) => { setPhotoPosition(adjustingIdx, pos); setAdjustingIdx(null); }}
          onCancel={() => setAdjustingIdx(null)}
        />
      )}
    </div>
  );
};

// Focal-point picker. Click anywhere on the image to set the (X%, Y%) point
// that should remain centered when the cover crops the image. Live preview
// uses the same `object-cover` + `objectPosition` rules as the public site
// so what you see is what visitors will see.
const FocalPointModal = ({ src, initialPosition, onSave, onCancel }) => {
  const [pos, setPos] = useState(initialPosition || '50% 50%');

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const clamp = (v) => Math.max(0, Math.min(100, v));
    setPos(`${Math.round(clamp(x))}% ${Math.round(clamp(y))}%`);
  };

  const [xStr, yStr] = pos.split(/\s+/);
  const xPct = parseFloat(xStr) || 50;
  const yPct = parseFloat(yStr) || 50;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="focal-point-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold mb-1">🎯 Adjust focal point</h3>
          <p className="text-sm text-gray-600 mb-4">
            Click anywhere on the photo below to mark the part that must stay in frame
            (usually the face). The previews on the right show how the cropped card
            will look on the public Impact Stories page.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Editable photo with crosshair */}
            <div className="md:col-span-2">
              <div
                className="relative w-full bg-gray-100 rounded-lg overflow-hidden cursor-crosshair select-none"
                style={{ maxHeight: '60vh' }}
                onClick={handleClick}
                data-testid="focal-point-canvas"
              >
                <img
                  src={src}
                  alt="adjust focal point"
                  className="w-full h-auto block max-h-[60vh] object-contain mx-auto"
                  draggable={false}
                />
                <div
                  className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white pointer-events-none"
                  style={{
                    left: `${xPct}%`,
                    top: `${yPct}%`,
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.65), 0 0 0 999px rgba(0,0,0,0.0)',
                    background: 'rgba(245, 158, 11, 0.45)',
                  }}
                />
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Position: <code className="px-1.5 py-0.5 rounded bg-gray-100">{pos}</code>
              </p>
            </div>

            {/* Live preview at the same aspect ratios used on the site */}
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Card cover (16:9)</p>
                <div className="aspect-[16/9] w-full rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                  <img
                    src={src}
                    alt="preview 16:9"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: pos }}
                    data-testid="focal-point-preview-16-9"
                  />
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Square thumbnail</p>
                <div className="aspect-square w-32 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                  <img
                    src={src}
                    alt="preview square"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: pos }}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPos('50% 50%')}
                className="text-sm text-gray-600 underline hover:text-gray-900"
                data-testid="focal-point-reset"
              >
                Reset to center
              </button>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              data-testid="focal-point-cancel"
              className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave(pos)}
              data-testid="focal-point-save"
              className="px-5 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary"
            >
              Save focal point
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpactStoriesManagement;
